import { nanoid } from 'nanoid';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  Td,
  Tr,
  Select,
  IconButton,
  Flex,
  CloseIcon,
} from '@frontend/components';

import { getDiscountDefs } from 'modules/quotes/quoteDetailsPage/data/reducer';
import Cell from 'modules/quotes/quoteItems/components/Table/Cell';

import discountsService from 'services/discountsService';

import { MANUAL_DISCOUNT, MULTIPLE_VALUES } from './data/constants';

const styles = {
  option: (defaultStyles, { value }) => {
    if (value === MANUAL_DISCOUNT) {
      return {
        ...defaultStyles,
        borderBottom: '1px solid #e5e5e5',
        fontStyle: 'italic',
      };
    } else {
      return defaultStyles;
    }
  },
};

export interface NewDiscountRowProps {
  onClose: () => void;
  onAdd: (discount: unknown) => void;
}

export const NewDiscountRow = ({ onClose, onAdd }: NewDiscountRowProps) => {
  const discountDefs = useSelector(getDiscountDefs);

  const options = [{ name: '+ Manual Discount', id: MANUAL_DISCOUNT }]
    .concat(discountDefs)
    .map(({ id, name }) => ({
      label: name,
      value: id,
    }));

  const addDiscount = useCallback(
    async (discountDef) => {
      let percentage: string | undefined = undefined;

      if (!discountDef.isManual) {
        const discountDefDetails = await discountsService.get(
          discountDef.discountDefId,
        );

        const basicPercentageFormulaMatch = (
          discountDefDetails?.formula?.p3lCode as string | null
        )
          ?.trim()
          .match(/^PERCENTAGE = (?<percentage>\d+)$/);

        percentage =
          basicPercentageFormulaMatch?.groups?.percentage ?? MULTIPLE_VALUES;
      }

      if (!percentage) {
        percentage = '0';
      }

      onAdd({ ...discountDef, percentage });
    },
    [onAdd],
  );

  return (
    <Tr>
      <Td py={0.25}>
        <Flex gap={1.5} alignItems="center">
          <Select
            autoFocus={true}
            defaultMenuIsOpen={true}
            filterOption={(option, inputValue) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) !==
                -1 || option.value === MANUAL_DISCOUNT
            }
            onChange={({ value, label }) => {
              if (value === MANUAL_DISCOUNT) {
                addDiscount({
                  name: 'Manual discount',
                  id: nanoid(),
                  isManual: true,
                });
              } else {
                addDiscount({
                  name: label,
                  id: nanoid(),
                  discountDefId: value,
                });
              }
            }}
            options={options}
            placeholder="Search for discount"
            styles={styles}
          />
          <IconButton flexShrink={0} aria-label="Remove Discount">
            <CloseIcon onClick={onClose} />
          </IconButton>
        </Flex>
      </Td>
      <Cell as={Td} display="table-cell" border="none" w="unset" />
    </Tr>
  );
};
